import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/system';
import type { MotionStyle } from 'motion/react';
import { CSSProperties } from 'react';

type KnownCSSProperties = {
	[K in keyof CSSProperties as string extends K ? never : K]: CSSProperties[K];
};

type ValidateCSS<T> = {
	[K in keyof T]: K extends keyof KnownCSSProperties
	? T[K]
	: `ERROR: '${K & string}' is not a valid CSS property`;
};

/**
 * Creates a strongly-typed stylesheet object where keys are preserved for autocomplete
 * and values are strictly typed to only allow valid CSS properties.
 * Invalid CSS properties will cause a type error with a helpful message.
 */
export function styleSheet<T extends Record<string, Record<string, unknown>>>(
	styles: { [K in keyof T]: ValidateCSS<T[K]> }
): T {
	return styles as T;
}

type KnownMotionProperties = {
	[K in keyof MotionStyle as string extends K ? never : K]: MotionStyle[K];
};

type ValidateMotionCSS<T> = {
	[K in keyof T]: K extends keyof KnownMotionProperties
	? T[K]
	: `ERROR: '${K & string}' is not a valid CSS or Motion property`;
};

/**
 * Creates a strongly-typed stylesheet object for Framer Motion components where keys
 * are preserved for autocomplete and values are strictly typed to only allow valid
 * CSS properties and Motion-specific properties (like transformStyle, perspective, etc.).
 * Invalid properties will cause a type error with a helpful message.
 */
export function motionStyleSheet<const T extends Record<string, Record<string, unknown>>>(
	styles: { [K in keyof T]: ValidateMotionCSS<T[K]> }
): { [K in keyof T]: { [P in keyof T[K]]: T[K][P] } } {
	return styles as { [K in keyof T]: { [P in keyof T[K]]: T[K][P] } };
}

/**
 * Creates a strongly-typed stylesheet object for MUI's sx prop where keys are preserved
 * for autocomplete and values are typed as SxProps<Theme>.
 * This allows using all MUI system properties including theme-aware properties like
 * bgcolor, paddingX, marginY, etc.
 *
 * You can also use theme callback functions for dynamic styling:
 * @example
 * const styles = muiStyleSheet({
 *   container: (theme) => ({
 *     bgcolor: theme.palette.primary.main,
 *     padding: theme.spacing(2),
 *   }),
 *   staticStyle: {
 *     display: 'flex',
 *     alignItems: 'center',
 *   }
 * });
 */
export function muiStyleSheet<T extends Record<string, SxProps<Theme>>>(
	styles: T
): T {
	return styles;
}

export function mergeSx(...args: (SxProps<Theme> | undefined)[]) {
	return args.reduce((acc, sx) => {
		return sx
			? [...acc, ...(Array.isArray(sx) ? sx : [sx])]
			: acc;
	}, [] as SxProps<Theme>[]);
}